import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "sonner";

import { toastError } from "@/lib/api-errors";

import * as api from "@/api/system_settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { SystemSettingsView } from "@/types/api";

type Person = { id: number; name: string };

/**
 * Задачи на проверку приёмки в Битрикс24.
 *
 * Исполнители выбираются ПИКЕРОМ, а не вводом числового ID: ошибка в одной
 * цифре ставит задачу чужому человеку, и заметят это, только когда товар
 * пролежит непроверенным. Список тянется из самого Битрикса — второй справочник
 * сотрудников в портале разъехался бы с ним на первом же увольнении.
 *
 * Первый в списке — исполнитель, остальные — соисполнители: у задачи Битрикса
 * ответственный ровно один.
 */
export function BitrixCard({ data }: { data: SystemSettingsView }): React.ReactElement {
  const qc = useQueryClient();
  const [webhook, setWebhook] = useState("");
  const [picker, setPicker] = useState<null | "lab" | "qc" | "watch" | "author">(null);
  const [q, setQ] = useState("");

  const users = useQuery({
    queryKey: ["bitrix-users"],
    queryFn: () => api.searchBitrixUsers(),
    enabled: picker !== null,
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: (patch: api.SystemSettingsUpdate) => api.updateSystemSettings(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["system-settings"] });
      toast.success("Сохранено");
    },
    onError: toastError("Ошибка сохранения"),
  });

  const lab: Person[] = data.bitrix_lab_users;
  const qcUsers: Person[] = data.bitrix_qc_users;
  const watchers: Person[] = data.bitrix_watchers;

  /** Список по цели пикера. Наблюдатели ОБЩИЕ для обоих направлений: они
   *  читают ход приёмки целиком, а не «свой отдел». */
  const listOf = (t: "lab" | "qc" | "watch"): Person[] =>
    t === "lab" ? lab : t === "qc" ? qcUsers : watchers;
  const patchOf = (t: "lab" | "qc" | "watch", next: Person[]): api.SystemSettingsUpdate =>
    t === "lab"
      ? { bitrix_lab_users: next }
      : t === "qc"
        ? { bitrix_qc_users: next }
        : { bitrix_watchers: next };

  const pick = (u: { id: number; name: string }) => {
    if (picker === null) return;
    if (picker === "author") {
      save.mutate({ bitrix_author_id: u.id, bitrix_author_name: u.name });
    } else {
      const list = listOf(picker);
      if (list.some((p) => p.id === u.id)) return;
      save.mutate(patchOf(picker, [...list, { id: u.id, name: u.name }]));
    }
    setPicker(null);
    setQ("");
  };

  const drop = (target: "lab" | "qc" | "watch", id: number) => {
    save.mutate(patchOf(target, listOf(target).filter((p) => p.id !== id)));
  };

  const row = (
    title: string,
    hint: string,
    target: "lab" | "qc" | "watch",
    list: Person[],
  ) => (
    <div>
      <div className="mb-1 text-xs font-medium text-muted-foreground">
        {title} <span className="font-normal">— {hint}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {list.map((p, i) => (
          <span
            key={p.id}
            className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-sm"
            title={
              target === "watch"
                ? "Наблюдатель — видит задачу, но не делает её"
                : i === 0
                  ? "Исполнитель"
                  : "Соисполнитель"
            }
          >
            {target !== "watch" && i === 0 && (
              <b className="text-[10px] uppercase text-primary">исп.</b>
            )}
            {p.name}
            <button
              type="button"
              onClick={() => drop(target, p.id)}
              className="text-muted-foreground"
              aria-label="Убрать"
            >
              ×
            </button>
          </span>
        ))}
        <Button size="sm" variant="outline" onClick={() => setPicker(target)}>
          + сотрудник
        </Button>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Битрикс24 — задачи на проверку приёмки</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={data.bitrix_enabled}
            onChange={(e) => save.mutate({ bitrix_enabled: e.target.checked })}
          />
          Ставить задачи после завершения приёмки
        </label>

        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">
            Вебхук{" "}
            {data.has_bitrix_webhook ? (
              <span className="text-emerald-700">— задан</span>
            ) : (
              <span className="text-destructive">— не задан, задачи не поставятся</span>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              placeholder="https://…bitrix24.ru/rest/1/код/"
              // Сам URL наружу не отдаётся — в нём код доступа; поле всегда
              // пустое, и заполнение видно отдельной подписью.
              autoComplete="off"
            />
            <Button
              disabled={!webhook.trim() || save.isPending}
              onClick={() => {
                save.mutate({ bitrix_webhook: webhook.trim() });
                setWebhook("");
              }}
            >
              Сохранить
            </Button>
          </div>
        </div>

        {/* Какие позиции куда — решают правила маршрутизации по группам
            номенклатуры (карточка выше), не категории. */}
        {row("Лаборатория", "группы с правилом «Лаборатория»", "lab", lab)}
        {row("ОКК", "группы с правилом «ОКК» и позиции без правила", "qc", qcUsers)}
        {/* Наблюдатель ≠ соисполнитель: он видит ход задачи, но не числится
            её исполнителем и не попадает в статистику работ. */}
        {row("Наблюдатели", "видят обе задачи, но не выполняют", "watch", watchers)}

        <div className="flex flex-wrap items-center gap-3">
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">Постановщик</div>
            <div className="flex items-center gap-1.5">
              <span className="rounded-full border px-2.5 py-1 text-sm">
                {data.bitrix_author_name ?? "— владелец вебхука"}
              </span>
              <Button size="sm" variant="outline" onClick={() => setPicker("author")}>
                выбрать
              </Button>
            </div>
          </div>
          <label className="text-xs text-muted-foreground">
            Срок, часов от приёмки
            <Input
              type="number"
              min={0}
              className="mt-1 w-28"
              defaultValue={data.bitrix_deadline_hours}
              onBlur={(e) =>
                save.mutate({ bitrix_deadline_hours: Number(e.target.value) || 0 })
              }
            />
          </label>
        </div>

        {picker && (
          <div className="rounded-md border p-2">
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Фамилия"
              className="mb-2"
            />
            <div className="max-h-56 space-y-1 overflow-y-auto">
              {users.isLoading && <p className="text-xs text-muted-foreground">Загрузка…</p>}
              {users.isError && (
                <p className="text-xs text-destructive">
                  Не удалось получить сотрудников — проверьте вебхук.
                </p>
              )}
              {(users.data ?? [])
                .filter((u) => !q || u.name.toLowerCase().includes(q.toLowerCase()))
                .slice(0, 40)
                .map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => pick(u)}
                    className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-accent"
                  >
                    {u.name}
                    <span className="ml-2 text-xs text-muted-foreground">{u.position}</span>
                  </button>
                ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setPicker(null)}>
              Отмена
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
